import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ClientDetailResponse, CreateCaseNoteRequest, caseNotesApi } from "@/api";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { CaseNoteFormValues } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/CaseNoteForm.types";
import { useCaseNoteForm } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/hooks/useCaseNoteForm";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useCaseNoteCreation(client: ClientDetailResponse, onClose: () => void) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserContext();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const {
    typeOptions,
    locationOptions,
    resultOptions,
    appointmentStatusOptions,
    isConversationType,
    validationSchema,
  } = useCaseNoteForm();

  const createCaseNoteMutation = useMutation({
    mutationFn: (request: CreateCaseNoteRequest) =>
      caseNotesApi.createCaseNote({ clientId: client.clientId, createCaseNoteRequest: request }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientOverview, client.clientId] });
      showSuccess(t("clients.client.caseNotes.creationDialog.successfullyAdded"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<CaseNoteFormValues>({
    initialValues: {
      type: "",
      date: new Date(),
      time: new Date(),
      assignedCounselorId: currentUser.counselorId ?? "",
      location: null,
      content: "",
      result: null,
      appointmentStatus: null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: CreateCaseNoteRequest = {
        type: values.type,
        date: values.date,
        time: values.time,
        assignedCounselorId: values.assignedCounselorId,
        location: values.location,
        content: values.content,
        result: values.result,
        appointmentStatus: isConversationType(values.type) ? values.appointmentStatus : null,
      };

      try {
        await createCaseNoteMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik, typeOptions, locationOptions, resultOptions, appointmentStatusOptions, isConversationType };
}
